import { MapPin, Phone, Clock, Star } from "lucide-react";
import { MaskLines, Reveal, Eyebrow } from "@/components/Motion";
import { CTAPair } from "@/components/CTAButtons";
import { SocialLinks } from "@/components/Footer";
import { SITE, HOURS } from "@/data/site";
import { useOpenStatus } from "@/hooks/useOpenStatus";

const InfoCard = ({ icon: Icon, label, children, testId }) => (
  <Reveal>
    <div className="h-full rounded-[28px] border border-cocoa/15 bg-white/60 p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(27,17,13,0.35)]" data-testid={testId}>
      <span className="grid h-11 w-11 place-items-center rounded-full bg-hibiscus text-cream"><Icon className="h-5 w-5" /></span>
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-cocoa/70">{label}</p>
      <div className="mt-2">{children}</div>
    </div>
  </Reveal>
);

export default function Visit() {
  const { weekday, label, isOpen } = useOpenStatus();
  return (
    <>
      <section className="relative overflow-hidden bg-cocoa pb-16 pt-40 text-cream" data-testid="visit-hero">
        <div className="pointer-events-none absolute -left-32 top-10 h-[420px] w-[420px] rounded-full bg-hibiscus/25 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Eyebrow className="text-mango">Visit us · Contáctanos</Eyebrow>
          <MaskLines testId="visit-title" className="mt-5 font-display text-6xl leading-[0.95] sm:text-8xl lg:text-[9rem]" lines={[<><em className="text-mango">Visítanos.</em></>]} />
          <p className="mt-6 max-w-xl text-lg text-cream/80">Pull up a chair or grab it to go. Either way, you'll be treated like family.</p>
          <CTAPair prefix="visit-hero" className="mt-10" />
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-4 md:grid-cols-3">
            <InfoCard icon={MapPin} label="Address" testId="visit-address">
              <p className="font-display text-2xl leading-snug">{SITE.street}<br />{SITE.cityLine}</p>
            </InfoCard>
            <InfoCard icon={Phone} label="Phone · Takeout" testId="visit-phone">
              <a href={SITE.tel} className="font-display text-3xl transition-colors hover:text-hibiscus">{SITE.phone}</a>
              <p className="mt-2 text-sm text-cocoa/75">Dine-in · Takeout · Lunch spot · $</p>
            </InfoCard>
            <InfoCard icon={Star} label="Google rating" testId="visit-rating">
              <p className="font-display text-3xl">{SITE.rating} <span className="text-mango">★</span></p>
              <p className="mt-2 text-sm text-cocoa/75">Across {SITE.reviews} Google reviews</p>
            </InfoCard>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <div className="h-full rounded-[28px] bg-cocoa p-7 text-cream sm:p-10" data-testid="visit-hours">
                <div className="flex items-center justify-between gap-4">
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/70"><Clock className="h-4 w-4" /> Horario</p>
                  <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${isOpen ? "bg-palm" : "bg-hibiscus"}`} data-testid="visit-open-status">{label.split(" · ")[0]}</span>
                </div>
                <ul className="mt-8">
                  {HOURS.map((h) => (
                    <li key={h.day} className={`flex justify-between border-b border-cream/10 py-3.5 ${h.short === weekday ? "text-mango" : ""}`} data-testid={`visit-hours-${h.short.toLowerCase()}`}>
                      <span className="font-display text-lg">{h.day}</span>
                      <span className={h.open ? "" : "italic text-cream/60"}>{h.open ? "10:00 AM – 6:00 PM" : "Closed"}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/70">Síguenos</p>
                  <SocialLinks className="mt-4" />
                </div>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-7" delay={0.1}>
              <div className="relative h-[420px] overflow-hidden rounded-[28px] border border-cocoa/15 lg:h-full lg:min-h-[560px]">
                <iframe title="Map to Sabor Latino" src={SITE.mapEmbed} className="absolute inset-0 h-full w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="visit-map" />
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-16 flex flex-col justify-between gap-8 border-t border-cocoa/15 pt-12 md:flex-row md:items-center">
            <p className="max-w-xl font-display text-3xl sm:text-4xl">On Cesar E. Chavez Ave SW — <em className="text-hibiscus">see you soon.</em></p>
            <CTAPair prefix="visit-bottom" dark />
          </Reveal>
        </div>
      </section>
    </>
  );
}
